/**
 * Keccak-256 (the ORIGINAL Keccak padding, `0x01`, not NIST SHA3-256's `0x06`).
 *
 * The hash Ethereum uses everywhere: ABI selectors and event topics (backend) and the
 * EIP-55 address checksum (frontend). One copy, written by hand on purpose: neither
 * package has an `ethers` / `viem` / `js-sha3` dependency, and Node's `sha3-256` is the
 * FIPS-202 variant, which yields different digests.
 *
 * BigInt lanes (not 32-bit hi/lo splitting): the inputs are a few short strings per
 * request, so clarity beats throughput, and a wrong-but-fast hash would silently
 * reject valid wallet addresses.
 *
 * Verified against published vectors in the frontend `keccak256.test.ts` and the
 * backend `contract-knowledge.unit.test.ts`.
 */

/** Keccak lanes are 64 bits; BigInt is unbounded, so every result is re-masked. */
const LANE_MASK_64_BITS = (1n << 64n) - 1n

/** Keccak-f[1600] has 25 lanes, indexed `x + 5 * y`. */
const LANE_COUNT = 25

/** Keccak-256 absorbs 1088 bits at a time: (1600 - 2 * 256) / 8 = 136 bytes. */
const RATE_IN_BYTES = 136

/** Keccak-f[1600] performs 24 rounds. */
const ROUND_COUNT = 24

/** Round constants for the ι (iota) step, from the Keccak reference spec. */
const IOTA_ROUND_CONSTANTS: readonly bigint[] = [
  0x0000000000000001n,
  0x0000000000008082n,
  0x800000000000808an,
  0x8000000080008000n,
  0x000000000000808bn,
  0x0000000080000001n,
  0x8000000080008081n,
  0x8000000000008009n,
  0x000000000000008an,
  0x0000000000000088n,
  0x0000000080008009n,
  0x000000008000000an,
  0x000000008000808bn,
  0x800000000000008bn,
  0x8000000000008089n,
  0x8000000000008003n,
  0x8000000000008002n,
  0x8000000000000080n,
  0x000000000000800an,
  0x800000008000000an,
  0x8000000080008081n,
  0x8000000000008080n,
  0x0000000080000001n,
  0x8000000080008008n,
]

/** Rotation offsets for the ρ (rho) step, laid out as `r[x][y]` at `x + 5 * y`. */
const RHO_ROTATION_OFFSETS: readonly number[] = [
  0, 1, 62, 28, 27, 36, 44, 6, 55, 20, 3, 10, 43, 25, 39, 41, 45, 15, 21, 8, 18, 2, 61, 56, 14,
]

const rotateLaneLeft = (lane: bigint, bitCount: number): bigint =>
  bitCount === 0
    ? lane
    : ((lane << BigInt(bitCount)) | (lane >> BigInt(64 - bitCount))) & LANE_MASK_64_BITS

/** One full Keccak-f[1600] permutation, applied in place to `state`. */
const permuteKeccakF1600 = (state: bigint[]): void => {
  const columnParities = new Array<bigint>(5).fill(0n)
  const thetaDeltas = new Array<bigint>(5).fill(0n)
  const permutedLanes = new Array<bigint>(LANE_COUNT).fill(0n)

  for (let round = 0; round < ROUND_COUNT; round++) {
    // θ (theta): fold each column's parity into its neighbours.
    for (let x = 0; x < 5; x++) {
      columnParities[x] = state[x] ^ state[x + 5] ^ state[x + 10] ^ state[x + 15] ^ state[x + 20]
    }
    for (let x = 0; x < 5; x++) {
      thetaDeltas[x] = columnParities[(x + 4) % 5] ^ rotateLaneLeft(columnParities[(x + 1) % 5], 1)
    }
    for (let y = 0; y < 5; y++) {
      for (let x = 0; x < 5; x++) {
        state[x + 5 * y] = state[x + 5 * y] ^ thetaDeltas[x]
      }
    }

    // ρ (rho) + π (pi): rotate every lane, then move it to (y, 2x + 3y).
    for (let y = 0; y < 5; y++) {
      for (let x = 0; x < 5; x++) {
        const sourceIndex = x + 5 * y
        const destinationIndex = y + 5 * ((2 * x + 3 * y) % 5)
        permutedLanes[destinationIndex] = rotateLaneLeft(
          state[sourceIndex],
          RHO_ROTATION_OFFSETS[sourceIndex],
        )
      }
    }

    // χ (chi): the only non-linear step — lane ^= (~next & nextNext) per row.
    for (let y = 0; y < 5; y++) {
      for (let x = 0; x < 5; x++) {
        state[x + 5 * y] =
          permutedLanes[x + 5 * y] ^
          (~permutedLanes[((x + 1) % 5) + 5 * y] &
            LANE_MASK_64_BITS &
            permutedLanes[((x + 2) % 5) + 5 * y])
      }
    }

    // ι (iota): break the round symmetry.
    state[0] = state[0] ^ IOTA_ROUND_CONSTANTS[round]
  }
}

/** Absorb one 136-byte block into the state (lanes are little-endian). */
const absorbBlock = (state: bigint[], block: Uint8Array): void => {
  for (let laneIndex = 0; laneIndex < RATE_IN_BYTES / 8; laneIndex++) {
    let lane = 0n
    for (let byteIndex = 7; byteIndex >= 0; byteIndex--) {
      lane = (lane << 8n) | BigInt(block[laneIndex * 8 + byteIndex])
    }
    state[laneIndex] = state[laneIndex] ^ lane
  }
  permuteKeccakF1600(state)
}

/**
 * Keccak-256 of a UTF-8 string or of raw bytes, as 64 lowercase hex characters (no `0x`).
 */
export const keccak256Hex = (input: string | Uint8Array): string => {
  const message = typeof input === 'string' ? new TextEncoder().encode(input) : input
  const state = new Array<bigint>(LANE_COUNT).fill(0n)

  // Original Keccak padding: `0x01` right after the message, `0x80` on the last
  // byte of the block. NIST SHA3-256 uses `0x06` here — swapping the two would
  // produce a plausible-looking but completely different digest.
  const paddedLength = (Math.floor(message.length / RATE_IN_BYTES) + 1) * RATE_IN_BYTES
  const padded = new Uint8Array(paddedLength)
  padded.set(message)
  padded[message.length] = 0x01
  padded[paddedLength - 1] = padded[paddedLength - 1] | 0x80

  for (let offset = 0; offset < paddedLength; offset += RATE_IN_BYTES) {
    absorbBlock(state, padded.subarray(offset, offset + RATE_IN_BYTES))
  }

  // Squeeze: 256 bits = the first 4 lanes, little-endian.
  let digestHex = ''
  for (let laneIndex = 0; laneIndex < 4; laneIndex++) {
    let lane = state[laneIndex]
    for (let byteIndex = 0; byteIndex < 8; byteIndex++) {
      digestHex += Number(lane & 0xffn)
        .toString(16)
        .padStart(2, '0')
      lane = lane >> 8n
    }
  }
  return digestHex
}
